import { get } from '../api/api'
import { showMessage } from './snackbar'
import { useAppStore } from '../store/appStore'
import { ROUTES } from '../routes'
import i18n from '../i18n'

export const isAxiosNetworkError = (error: unknown): boolean => {
    const e = error as Record<string, unknown>
    return !!e && e['isAxiosError'] === true && e['message'] === 'Network Error'
}

export const isAxiosServerError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false
    const e = error as Record<string, unknown>
    const status = (e['response'] as Record<string, unknown> | undefined)?.['status']
    return e['isAxiosError'] === true && typeof status === 'number' && status >= 500
}

export const isAxiosRateLimited = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false
    const e = error as Record<string, unknown>
    const status = (e['response'] as Record<string, unknown> | undefined)?.['status']
    return e['isAxiosError'] === true && status === 429
}

export const sanitizeAxiosError = (error: unknown): unknown => {
    if (error && typeof error === 'object') {
        const e = error as Record<string, unknown>
        if (e['isAxiosError'] && e['config'] && typeof e['config'] === 'object') {
            const config = e['config'] as Record<string, unknown>
            if (config['data']) {
                config['data'] = '[REDACTED]'
            }
            // Manually-attached auth headers (e.g. signupSession.ts's SSO bearer token) never
            // go through api.ts's interceptor, so nothing else strips them before this error
            // reaches console.warn/Sentry.
            if (config['headers'] && typeof config['headers'] === 'object') {
                const headers = config['headers'] as Record<string, unknown>
                for (const key of Object.keys(headers)) {
                    if (key.toLowerCase() === 'authorization') headers[key] = '[REDACTED]'
                }
            }
        }
    }
    return error
}

const isAdapterUnavailable = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false
    const e = error as Record<string, unknown>
    const status = (e['response'] as Record<string, unknown> | undefined)?.['status']
    return (
        (e['isAxiosError'] === true && (status === 503 || e['code'] === 'ECONNABORTED')) || isAxiosNetworkError(error)
    )
}

type AdapterErrorOptions = {
    navigate?: (path: string) => void
}

// Handles known adapter error cases (503, 401, 404) with user-facing feedback.
// Re-throws for unexpected errors so callers can handle or log them.
export const handleAdapterError = async (
    error: unknown,
    adapter: string,
    options?: AdapterErrorOptions
): Promise<null> => {
    if (isAdapterUnavailable(error)) {
        showMessage(i18n.t('adapter.connection_error', { adapter }), 'error')
        return null
    }

    const status = (error as Record<string, unknown> | null)?.['response'] as Record<string, unknown> | undefined
    const statusCode = status?.['status']

    if (statusCode === 401) {
        try {
            const initResponse = await get<{ auth_uri?: string }>(`/adapters/${adapter}/init`)
            if (initResponse?.auth_uri) {
                showMessage(i18n.t('adapter.reconnect_required', { adapter }), 'warning')
                options?.navigate?.(ROUTES.integrations.list.path)
            } else {
                useAppStore.setState({ isAdapterConnected: true })
                showMessage(i18n.t('adapter.reconnected', { adapter }), 'success')
            }
        } catch {
            showMessage(i18n.t('adapter.reconnect_failed', { adapter }), 'error')
            options?.navigate?.(ROUTES.integrations.list.path)
        }
        return null
    }

    if (statusCode === 404) {
        return null
    }

    throw error
}
